import { useMemo } from 'react'
import { useParams, useHistory } from 'react-router'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle'
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { LearningPathCard } from 'components/ui/LearningPathCard'
import getLearningPath from 'api/learning-paths/getLearningPath'
import Loader from 'components/ui/loader/Loader'
import { ILearningPathCard } from 'models/learningPathCard.model'
import { CourseVerticalCard } from 'components/ui/CourseCards'
import { GoBackButton } from 'components/home/components/Buttons/GoBackButton'
import { paths } from 'routes/paths'
import { useIsMobile } from 'hooks/useIsMobile'
import styles from './LearningPath.module.scss'
import {
  Alert,
  AlertItem,
  Collections,
  Collection,
  CollectionName,
} from './styled'

interface Params {
  id: string
}

export const LearningPath = () => {
  const params = useParams<Params>()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const history = useHistory()
  const { data, isLoading } = useSWR(
    {
      key: `learning-path/${params.id}`,
      id: params.id,
    },
    async (args) => {
      return getLearningPath({ id: args.id })
    }
  )

  const learningPathParsed = useMemo((): ILearningPathCard | null => {
    if (!data) return null
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      pictureUrl: data.pictureUrl,
      completedCourses: data.completedCourses,
      coursesCount: data.coursesCount,
      optional: data.optional,
      optionalCoursesCount: data.optionalCoursesCount,
      requiredCoursesCount: data.requiredCoursesCount,
      completedRequiredCourses: data.completedRequiredCourses,
      completedOptionalCourses: data.completedOptionalCourses,
      enrol: data.enrol,
    }
  }, [data])

  if (isLoading) return <Loader color />
  return (
    <div className={styles.container}>
      <GoBackButton to={paths.myCourses.learningPaths} />
      {data && data.prerequisites.length > 0 && (
        <Alert>
          <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />
          {t('newhome.learningPathDetail.alert')}
          {data?.prerequisites.map((prerequisite, index) => (
            <AlertItem key={index}>
              <FontAwesomeIcon icon={faCircle as IconProp} />
              <span
                onClick={() =>
                  history.push(`/learning-path/${prerequisite.id}`)
                }
              >
                {prerequisite.name}
              </span>
            </AlertItem>
          ))}
        </Alert>
      )}
      {learningPathParsed && (
        <div className={isMobile ? styles.mobileLearningPathWrapper : ''}>
          <LearningPathCard learningPath={learningPathParsed} />
        </div>
      )}
      <Collections>
        {data?.sets.map((set, index) => (
          <Collection key={index}>
            <CollectionName>{set.name}</CollectionName>
            <div
              className={`${styles.coursesContainer} ${
                isMobile ? styles.mobileCoursesWrapper : ''
              }`}
            >
              {set.courses.map((course, index) => (
                <CourseVerticalCard
                  key={'av' + index}
                  course={course.course}
                  enrol={course?.enrol || null}
                  enrolled={!!course.enrol}
                />
              ))}
            </div>
          </Collection>
        ))}
      </Collections>
    </div>
  )
}
